import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Stack, Typography, IconButton, CircularProgress, Alert } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

export default function ImageDropUpload({ value, onChange, label = 'Imagen', folder = 'images' }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  const handleUpload = useCallback(
    async (file) => {
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        setError('El archivo debe ser una imagen.');
        return;
      }
      setError(null);
      setUploading(true);
      setProgress(0);
      try {
        const storage = getStorage();
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const filename = `${folder}/${Date.now()}_${Math.floor(Math.random() * 1e6)}_${safeName}`;
        const storageRef = ref(storage, filename);
        const task = uploadBytesResumable(storageRef, file);

        await new Promise((resolve, reject) => {
          task.on(
            'state_changed',
            (snap) => setProgress((snap.bytesTransferred / snap.totalBytes) * 100),
            reject,
            resolve
          );
        });

        const url = await getDownloadURL(task.snapshot.ref);
        onChange(url);
      } catch (e) {
        console.error('Image upload failed:', e);
        setError('Error subiendo la imagen. Intenta de nuevo.');
      }
      setUploading(false);
      setProgress(0);
    },
    [folder, onChange]
  );

  const onDrop = useCallback(
    (acceptedFiles) => {
      if (acceptedFiles && acceptedFiles[0]) {
        handleUpload(acceptedFiles[0]);
      }
    },
    [handleUpload]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'] },
    multiple: false,
    disabled: uploading,
  });

  const handleRemove = (e) => {
    e.stopPropagation();
    onChange('');
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>{label}</Typography>

      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.300',
          borderRadius: 2,
          p: value ? 1 : 4,
          textAlign: 'center',
          cursor: uploading ? 'wait' : 'pointer',
          bgcolor: isDragActive ? 'action.hover' : 'background.paper',
          transition: 'all 0.2s',
          minHeight: 160,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <input {...getInputProps()} />

        {uploading ? (
          <Stack alignItems="center" spacing={1}>
            <CircularProgress variant="determinate" value={progress} />
            <Typography variant="caption" color="text.secondary">
              Subiendo... {Math.round(progress)}%
            </Typography>
          </Stack>
        ) : value ? (
          <Box sx={{ position: 'relative', width: '100%' }}>
            <Box
              component="img"
              src={value}
              alt="preview"
              sx={{
                maxWidth: '100%',
                maxHeight: 240,
                borderRadius: 1,
                objectFit: 'contain',
                display: 'block',
                margin: '0 auto',
              }}
            />
            <IconButton
              onClick={handleRemove}
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                bgcolor: 'background.paper',
                boxShadow: 1,
                '&:hover': { bgcolor: 'background.paper' },
              }}
              size="small"
            >
              <DeleteIcon fontSize="small" color="error" />
            </IconButton>
          </Box>
        ) : (
          <Stack alignItems="center" spacing={1}>
            <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {isDragActive ? 'Suelta la imagen aquí...' : 'Arrastra una imagen o haz click para seleccionar'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              PNG, JPG, GIF, WEBP
            </Typography>
          </Stack>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
    </Box>
  );
}
